import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import StudyStayLogo from '../components/StudyStayLogo';
import ScrollProgress from '../components/ScrollProgress';
import Reveal from '../components/Reveal';
import ParallaxBackground from '../components/ParallaxBackground';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { SUBSCRIPTION_PLANS } from '../services/payment.service';
import {
  Building2,
  CheckCircle,
  XCircle,
  ArrowRight,
  ArrowLeft,
  Shield,
  BarChart3,
  MessageCircle,
  Calendar,
  FileText,
  Euro,
  Instagram,
  Sparkles,
  Mail
} from 'lucide-react';
import toast from 'react-hot-toast';

const landlordBenefits = [
  {
    icon: Building2,
    title: 'All-in-one rental dashboard',
    description: 'Manage listings, applicants, messages, and contracts from a single, clean workspace.',
  },
  {
    icon: MessageCircle,
    title: 'Faster, clearer communication',
    description: 'Chat with students and keep everything documented in one conversation thread.',
  },
  {
    icon: FileText,
    title: 'Professional digital contracts',
    description: 'Send, sign, and store contracts securely with full audit trails.',
  },
  {
    icon: Calendar,
    title: 'Viewings made simple',
    description: 'Schedule visits effortlessly and keep a clear overview of interested tenants.',
  },
  {
    icon: Shield,
    title: 'Verified & secure',
    description: 'Verified profiles and secure storage for your documents and agreements.',
  },
  {
    icon: BarChart3,
    title: 'Performance insights',
    description: 'Track listing performance and optimize your rental flow.',
  },
];

const EARLY_PRICES = { pro: 19, agency: 79 };
const AUDIENCE_MAP = {
  listing_only_private: 'private',
  listing_only_agency: 'agency',
  pro: 'private',
  agency: 'agency',
};
const pricingPlans = SUBSCRIPTION_PLANS
  .filter((p) => p.id !== 'starter')
  .map((p) => ({
    id: p.id,
    audience: AUDIENCE_MAP[p.id] || 'private',
    name: p.name,
    subtitle: AUDIENCE_MAP[p.id] === 'agency' ? 'For agencies' : 'For private landlords',
    earlyPrice: EARLY_PRICES[p.id] ?? p.price,
    regularPrice: p.price,
    badge: p.id.startsWith('listing_only') ? 'Always free' : EARLY_PRICES[p.id] ? 'Early access' : '',
    isFree: !!p.isFree,
    features: p.features,
    notIncluded: p.notIncluded || [],
  }));

export default function ComingSoonLandlord() {
  const navigate = useNavigate();
  const [pricingAudience, setPricingAudience] = useState('private');
  const [submittingCall, setSubmittingCall] = useState(false);
  const [callForm, setCallForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    notes: ''
  });

  const handleCallInputChange = (event) => {
    const { name, value } = event.target;
    setCallForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleChooseLater = () => {
    toast.success('Gratis plan geselecteerd.');
  };

  const handleChoosePlan = (plan) => {
    const landlordType = plan.audience === 'agency' ? 'agency' : 'private';
    const planId = plan.id || 'starter';
    sessionStorage.setItem('earlyAccessPlan', planId);
    sessionStorage.setItem('earlyAccessLandlordType', landlordType);
    navigate(
      `/user-type-login?mode=register&userType=landlord&landlordType=${landlordType}&plan=${planId}&source=coming-soon`
    );
  };

  const visiblePricingPlans = pricingPlans.filter(
    (plan) => plan.audience === pricingAudience
  );

  const handleCallRequestSubmit = async (event) => {
    event.preventDefault();
    if (submittingCall) return;

    const fullName = callForm.fullName.trim();
    const email = callForm.email.trim().toLowerCase();
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    if (!fullName || !email) {
      toast.error('Vul minimaal naam en e-mail in.');
      return;
    }
    if (!emailValid) {
      toast.error('Voer een geldig e-mailadres in.');
      return;
    }

    try {
      setSubmittingCall(true);
      await addDoc(collection(db, 'waitlist'), {
        email,
        role: 'landlord',
        source: 'coming-soon',
        callRequested: true,
        callType: '30min-onboarding',
        fullName,
        phone: callForm.phone.trim(),
        company: callForm.company.trim(),
        notes: callForm.notes.trim(),
        createdAt: serverTimestamp()
      });

      toast.success('Top! Je ontvangt binnenkort een e-mail om je 30 min call te boeken.');
      setCallForm({
        fullName: '',
        email: '',
        phone: '',
        company: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error creating call request:', error);
      toast.error('Kon aanvraag niet verzenden. Probeer opnieuw.');
    } finally {
      setSubmittingCall(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-primary-50 relative overflow-hidden">
      <ScrollProgress />
      <ParallaxBackground />

      <header className="relative z-10 pt-8 pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex items-center justify-between">
              <Link to="/coming-soon" className="flex items-center gap-3">
                <StudyStayLogo className="w-12 h-12" />
                <h1 className="text-2xl md:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-500 to-primary-600">
                  StudyStay
                </h1>
              </Link>
              <div className="flex items-center gap-3">
                <Link
                  to="/coming-soon/landlord"
                  className="px-4 py-2 rounded-xl bg-primary-600 text-white font-semibold shadow-sm hover:shadow-md transition-all duration-200"
                >
                  Landlord
                </Link>
                <Link
                  to="/coming-soon/partnerships"
                  className="px-4 py-2 rounded-xl bg-white text-gray-700 font-semibold shadow-sm hover:shadow-md border border-gray-200 transition-all duration-200"
                >
                  Partnerships
                </Link>
                <a
                  href="https://www.instagram.com/studystay_app?igsh=eWZjcDYwbW9yZmYw&utm_source=qr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110"
                  aria-label="Follow us on Instagram"
                >
                  <Instagram className="w-5 h-5 text-primary-600" />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </header>

      <section className="relative z-10 pt-10 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <Reveal>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-5 leading-tight">
              Professional renting, without the busywork.
            </h2>
          </Reveal>
          <Reveal delay={150}>
            <p className="text-lg md:text-xl text-gray-700 mb-8">
              StudyStay helps landlords manage listings, tenants, and contracts in one secure platform.
            </p>
          </Reveal>
          <Reveal delay={250}>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <span className="px-4 py-2 rounded-full bg-white text-gray-700 font-semibold shadow-sm border border-gray-200">
                Verified profiles
              </span>
              <span className="px-4 py-2 rounded-full bg-white text-gray-700 font-semibold shadow-sm border border-gray-200">
                Digital signatures
              </span>
              <span className="px-4 py-2 rounded-full bg-white text-gray-700 font-semibold shadow-sm border border-gray-200">
                Centralized messaging
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8 bg-white/60 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Why landlords choose StudyStay
            </h3>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {landlordBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Reveal key={benefit.title} delay={index * 100}>
                  <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                    <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-primary-600" />
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h4>
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-10">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Pricing</h3>
              <p className="text-gray-600">
                Kies nu je early access prijs. De normale prijs is doorgestreept.
              </p>
              <div className="mt-5 inline-flex items-center rounded-xl bg-white border border-gray-200 p-1 shadow-sm">
                <button
                  type="button"
                  onClick={() => setPricingAudience('private')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    pricingAudience === 'private'
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Particulier
                </button>
                <button
                  type="button"
                  onClick={() => setPricingAudience('agency')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    pricingAudience === 'agency'
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Immo
                </button>
              </div>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6">
            {visiblePricingPlans.map((plan, index) => (
              <Reveal key={plan.name} delay={index * 150}>
                <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-2xl font-bold text-gray-900">{plan.name}</h4>
                      <p className="text-sm text-gray-600">{plan.subtitle}</p>
                    </div>
                    <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">
                      {plan.badge}
                    </span>
                  </div>
                  <div className="flex items-end gap-3 mb-6">
                    {plan.isFree ? (
                      <div className="text-4xl font-bold text-emerald-600">Free</div>
                    ) : (
                      <>
                        <div className="text-4xl font-bold text-gray-900 flex items-center gap-1">
                          <Euro className="w-6 h-6 text-primary-600" />
                          {plan.earlyPrice}
                        </div>
                        {plan.regularPrice !== plan.earlyPrice && (
                          <div className="text-sm text-gray-400 line-through">€{plan.regularPrice}</div>
                        )}
                        <div className="text-sm text-gray-600">/maand</div>
                      </>
                    )}
                  </div>
                  <ul className="space-y-3">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-center text-gray-700">
                        <CheckCircle className="w-4 h-4 text-primary-600 mr-2" />
                        {feature}
                      </li>
                    ))}
                    {(plan.notIncluded || []).map((feature) => (
                      <li key={`not-${feature}`} className="flex items-center text-gray-500">
                        <XCircle className="w-4 h-4 text-gray-400 mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => handleChoosePlan(plan)}
                    className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary-600 text-white font-semibold hover:bg-primary-700 transition-colors"
                  >
                    <Sparkles className="w-4 h-4" />
                    Kies dit plan
                  </button>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={250}>
            <div className="mt-8 rounded-2xl border border-primary-100 bg-gradient-to-r from-primary-50 to-blue-50 p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <p className="text-sm text-primary-700 font-semibold">Nog niet klaar om te betalen?</p>
                  <p className="text-sm text-gray-600">Start direct met het gratis plan en upgrade wanneer jij wil.</p>
                </div>
                <button
                  type="button"
                  onClick={handleChooseLater}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white text-gray-800 border border-gray-200 font-semibold hover:bg-gray-50 transition-colors"
                >
                  Try for free
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="call-booking-section" className="relative z-10 py-16 px-4 sm:px-6 lg:px-8 bg-white/70 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Boek een call
            </h3>
            <p className="text-gray-600 mb-10">
              Vul hieronder je gegevens in. Wij sturen je daarna meteen een boekingslink per e-mail.
            </p>
            <div className="max-w-2xl mx-auto mb-6 rounded-2xl border border-gray-200 bg-white p-7 text-left shadow-md">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-primary-600 text-white flex items-center justify-center">
                  <Calendar className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900">Persoonlijke intake call (30 min)</h4>
                  <p className="text-xs text-gray-500">Online - 1-op-1</p>
                </div>
              </div>

              <form onSubmit={handleCallRequestSubmit} className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-sm font-semibold text-gray-700">Naam *</span>
                    <input
                      type="text"
                      name="fullName"
                      value={callForm.fullName}
                      onChange={handleCallInputChange}
                      className="w-full mt-1 px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm font-semibold text-gray-700">E-mail *</span>
                    <input
                      type="email"
                      name="email"
                      value={callForm.email}
                      onChange={handleCallInputChange}
                      className="w-full mt-1 px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </label>
                </div>
                <label className="block">
                  <span className="text-sm font-semibold text-gray-700">Telefoon (optioneel)</span>
                  <input
                    type="text"
                    name="phone"
                    value={callForm.phone}
                    onChange={handleCallInputChange}
                    className="w-full mt-1 px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </label>
                <button
                  type="submit"
                  disabled={submittingCall}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary-600 text-white font-semibold hover:bg-primary-700 disabled:opacity-60 transition-colors"
                >
                  {submittingCall ? 'Verzenden...' : 'Boek mijn call'}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            </div>
          </Reveal>
        </div>
      </section>

      <footer className="relative z-10 border-t border-gray-200 py-8 px-4 sm:px-6 lg:px-8 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto text-center">
          <Reveal>
            <div className="flex items-center justify-center gap-2 mb-4">
              <StudyStayLogo className="w-6 h-6" />
              <span className="text-gray-700 font-semibold">StudyStay</span>
            </div>
            <p className="text-gray-600 font-medium mb-4">Coming Soon — Web & iOS</p>
            <div className="flex items-center justify-center gap-6 mb-4 flex-wrap">
              <a
                href="/privacy"
                className="text-sm text-gray-600 hover:text-primary-600 transition-colors duration-200"
              >
                Privacy Policy
              </a>
              <a
                href="/terms"
                className="text-sm text-gray-600 hover:text-primary-600 transition-colors duration-200"
              >
                Terms of Service
              </a>
            </div>
            <p className="text-sm text-gray-500">© 2026 Nolf Technologies OÜ. All rights reserved.</p>
          </Reveal>
        </div>
      </footer>
    </div>
  );
}
